import React, { useState, useEffect, useCallback } from 'react';
import './DentalChart.css';
import { useApp } from '../../../AppProvider';
import { useAuth } from '../../../AuthProvider';
import api from '../../../axiosInstance';
import logger from "../../../utils/logger";

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

const COND = {
  healthy:   { c: '#ffffff',  border: '#c4ccd2', l: 'Healthy'        },
  caries:    { c: '#e8554e',  border: '#e8554e', l: 'Caries / Decay' },
  filled:    { c: '#4a90d9',  border: '#4a90d9', l: 'Filled'         },
  crown:     { c: '#f6b81e',  border: '#e0a40c', l: 'Crown'          },
  rootcanal: { c: '#9b59b6',  border: '#9b59b6', l: 'Root Canal'     },
  implant:   { c: '#36a99e',  border: '#36a99e', l: 'Implant'        },
  missing:   { c: '#c4ccd2',  border: '#9aa7b1', l: 'Missing'        },
};

const CONDITIONS_LIST = Object.entries(COND).map(([k, v]) => ({
  value: k, label: v.l, color: v.c,
}));

const CATEGORIES = [
  { value: 'diagnostic',    label: 'Diagnostic'    },
  { value: 'preventive',    label: 'Preventive'    },
  { value: 'restorative',   label: 'Restorative'   },
  { value: 'endodontic',    label: 'Endodontic'    },
  { value: 'prosthodontic', label: 'Prosthodontic' },
  { value: 'periodontic',   label: 'Periodontic'   },
  { value: 'oralsurgery',   label: 'Oral Surgery'  },
  { value: 'orthodontic',   label: 'Orthodontic'   },
  { value: 'cosmetic',      label: 'Cosmetic'      },
];

const STATUSES = [
  { value: 'completed',   label: 'Completed'   },
  { value: 'planned',     label: 'Planned'     },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'cancelled',   label: 'Cancelled'   },
];

const SURFACES = ['Mesial', 'Distal', 'Occlusal', 'Incisal', 'Buccal', 'Lingual', 'Palatal', 'Labial'];

const DENTITION_CONFIG = {
  adult: {
    upper: [18,17,16,15,14,13,12,11,21,22,23,24,25,26,27,28],
    lower: [48,47,46,45,44,43,42,41,31,32,33,34,35,36,37,38],
    totalTeeth: 32, label: 'Adult · 32',
  },
  child: {
    upper: [55,54,53,52,51,61,62,63,64,65],
    lower: [85,84,83,82,81,71,72,73,74,75],
    totalTeeth: 20, label: 'Child · 20',
  },
  mixed: {
    upper: [16,55,54,53,12,11,21,22,63,64,65,26],
    lower: [46,85,84,83,42,41,31,32,73,74,75,36],
    totalTeeth: 24, label: 'Mixed',
  },
};

const CATEGORY_COLOR = {
  restorative:  'dc-tag--teal',
  preventive:   'dc-tag--teal',
  endodontic:   'dc-tag--red',
  prosthodontic:'dc-tag--teal',
  periodontic:  'dc-tag--amber',
  oralsurgery:  'dc-tag--red',
  orthodontic:  'dc-tag--amber',
  diagnostic:   'dc-tag--amber',
  cosmetic:     'dc-tag--teal',
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function getToothType(fdi) {
  const n    = parseInt(fdi, 10);
  const quad = Math.floor(n / 10);
  const pos  = n % 10;
  const prim = quad >= 5;
  if (prim) {
    if (pos <= 2) return 'incisor';
    if (pos === 3) return 'canine';
    return 'molar';
  }
  if (pos <= 2) return 'incisor';
  if (pos === 3) return 'canine';
  if (pos <= 5)  return 'premolar';
  return 'molar';
}

function isPrimary(fdi) {
  return Math.floor(parseInt(fdi, 10) / 10) >= 5;
}

function toothTypeName(type) {
  return { incisor:'Incisor', canine:'Canine', premolar:'Premolar', molar:'Molar' }[type] ?? 'Tooth';
}

function fmtDate(str) {
  if (!str) return '';
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' });
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Build a flat, date-sorted timeline from the procedures that involve the
 * given fdi. Multi-visit procedures contribute their `timeline[]` entries;
 * single-visit procedures contribute one entry from `dateOfPublish`.
 */
function buildTimeline(procedures, fdi) {
  if (!fdi || !procedures?.length) return [];

  const related = procedures.filter(p =>
    p.teeth?.some(t => String(t) === String(fdi))
  );

  const entries = [];

  related.forEach(proc => {
    if (proc.timeline?.length > 0) {
      proc.timeline.forEach(tl => {
        entries.push({
          date:     tl.date,
          title:    tl.title,
          subtitle: tl.notes ?? null,
          category: proc.category,
          procName: proc.procedureName,
        });
      });
    } else {
      entries.push({
        date:     proc.dateOfPublish,
        title:    proc.procedureName,
        subtitle: proc.details?.slice(0, 2).join(' · ') ?? null,
        category: proc.category,
        procName: proc.procedureName,
        isMain:   true,
      });
    }
  });

  // Oldest first (ascending)
  return entries.sort((a, b) => new Date(a.date ?? 0) - new Date(b.date ?? 0));
}

// ─────────────────────────────────────────────
// TOOTH SVGs
// ─────────────────────────────────────────────

const MolarSVG = ({ fill, stroke }) => (
  <svg width="30" height="42" viewBox="0 0 30 42">
    <path d="M9 23l-2 14M15 24v15M21 23l2 14" stroke="#cdd8df" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <g stroke={stroke} strokeWidth="1.4">
      <path className="dc-crown"
        d="M5 16c0-7 5-11 10-11s10 4 10 11c0 4-2 7-4 8-1 .5-2 .5-3 0-1 1-3 1-4 0-1 .5-2 .5-3 0-2-1-6-4-6-8z"
        fill={fill}/>
    </g>
  </svg>
);

const PremolarSVG = ({ fill, stroke }) => (
  <svg width="30" height="42" viewBox="0 0 30 42">
    <path d="M11 22l-1 15M19 22l1 15" stroke="#cdd8df" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <g stroke={stroke} strokeWidth="1.4">
      <path className="dc-crown"
        d="M7 16c0-6 4-10 8-10s8 4 8 10c0 4-2 6-4 7-1 .6-3 .6-4 0-2 1-8-1-8-7z"
        fill={fill}/>
    </g>
  </svg>
);

const CanineSVG = ({ fill, stroke }) => (
  <svg width="30" height="42" viewBox="0 0 30 42">
    <path d="M15 24v16" stroke="#cdd8df" strokeWidth="2.4" fill="none" strokeLinecap="round"/>
    <g stroke={stroke} strokeWidth="1.4">
      <path className="dc-crown"
        d="M15 5c5 3 8 7 8 12 0 5-3 8-8 8s-8-3-8-8c0-5 3-9 8-12z"
        fill={fill}/>
    </g>
  </svg>
);

const IncisorSVG = ({ fill, stroke }) => (
  <svg width="30" height="42" viewBox="0 0 30 42">
    <path d="M15 23v17" stroke="#cdd8df" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
    <g stroke={stroke} strokeWidth="1.4">
      <path className="dc-crown"
        d="M9 7c0-2 12-2 12 0v9c0 5-3 8-6 8s-6-3-6-8z"
        fill={fill}/>
    </g>
  </svg>
);

function ToothSVG({ toothType, condition, selected }) {
  const fill   = COND[condition]?.c ?? '#ffffff';
  const stroke = selected ? '#5cc4e8' : '#8aa0ad';
  const p = { fill, stroke };
  switch (toothType) {
    case 'molar':    return <MolarSVG    {...p} />;
    case 'premolar': return <PremolarSVG {...p} />;
    case 'canine':   return <CanineSVG   {...p} />;
    default:         return <IncisorSVG  {...p} />;
  }
}

// ─────────────────────────────────────────────
// TOOTH ITEM
// ─────────────────────────────────────────────

function ToothItem({ fdi, teethMap, isLower, selected, onSelect }) {
  const data      = teethMap[String(fdi)] ?? null;
  const condition = data?.condition ?? 'healthy';
  const hasNote   = !!data?.note;
  const type      = getToothType(fdi);

  return (
    <div
      className={[
        'dc-tooth',
        selected               ? 'dc-tooth--sel'     : '',
        condition === 'missing'? 'dc-tooth--missing'  : '',
      ].join(' ')}
      onClick={() => onSelect(fdi)}
      title={`Tooth ${fdi} · ${COND[condition]?.l}`}
    >
      {!isLower && <span className="dc-tooth__num">{fdi}</span>}
      <div style={isLower ? { transform:'rotate(180deg)' } : {}}>
        <ToothSVG toothType={type} condition={condition} selected={selected} />
      </div>
      {isLower && <span className="dc-tooth__num">{fdi}</span>}
      {hasNote && <span className="dc-tooth__badge" aria-hidden>!</span>}
    </div>
  );
}

// ─────────────────────────────────────────────
// ARCH ROW
// ─────────────────────────────────────────────

function Arch({ fdiList, teethMap, isLower, selectedFdi, onSelect }) {
  const mid = Math.floor(fdiList.length / 2);
  return (
    <div className="dc-arch">
      {fdiList.map((fdi, i) => (
        <React.Fragment key={fdi}>
          {i === mid && <div className="dc-arch__gap" />}
          <ToothItem
            fdi={fdi}
            teethMap={teethMap}
            isLower={isLower}
            selected={selectedFdi === fdi}
            onSelect={onSelect}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// STAT BOX
// ─────────────────────────────────────────────

function StatBox({ value, label, bg, icon }) {
  return (
    <div className="dc-stat">
      <div className="dc-stat__icon" style={{ background: bg }}>{icon}</div>
      <div>
        <div className="dc-stat__val">{value ?? '—'}</div>
        <div className="dc-stat__lbl">{label}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PROCEDURE CARD
// ─────────────────────────────────────────────

function ProcedureCard({ proc, onClick }) {
  const tagCls = CATEGORY_COLOR[proc.category] ?? 'dc-tag--amber';
  return (
    <div
      className="dc-proc-card dc-proc-card--clickable"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick?.()}
    >
      <div className="dc-proc-card__top">
        <span className="dc-proc-card__date">{fmtDate(proc.dateOfPublish)}</span>
        <span className="dc-proc-card__edit-hint">View / Edit →</span>
      </div>
      <h4 className="dc-proc-card__title">{proc.procedureName}</h4>
      {proc.cdtCode && (
        <p className="dc-proc-card__cdt">CDT: {proc.cdtCode}</p>
      )}
      {proc.teeth?.length > 0 && (
        <p className="dc-proc-card__row">
          <b>Teeth: </b>
          {proc.teeth.map(t => (
            <span key={t} className="dc-proc-card__tooth-tag">{t}</span>
          ))}
        </p>
      )}
      {proc.surfaces?.length > 0 && (
        <p className="dc-proc-card__row">
          <b>Surfaces: </b>
          <span className="dc-proc-card__surfaces">{proc.surfaces.join(' · ')}</span>
        </p>
      )}
      {proc.details?.map((d, i) => (
        <p key={i} className="dc-proc-card__detail">• {d}</p>
      ))}
      <div className="dc-proc-card__footer">
        <span className={`dc-tag ${tagCls}`}>{proc.category}</span>
        {proc.status === 'planned'     && <span className="dc-tag dc-tag--amber">Planned</span>}
        {proc.status === 'in-progress' && <span className="dc-tag dc-tag--amber">In Progress</span>}
        {proc.timeline?.length > 0 && (
          <span className="dc-proc-card__visits">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>
            </svg>
            {proc.timeline.length} visit{proc.timeline.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PROCEDURE DRAWER  (view + edit)
// ─────────────────────────────────────────────

function ProcedureEditDrawer({ proc, isOpen, onClose, onSaved, onDeleted }) {
  // ── view / edit modes ──
  const [editMode,     setEditMode]     = useState(false);

  // ── edit form fields ──
  const [procName,     setProcName]     = useState('');
  const [category,     setCategory]     = useState('restorative');
  const [status,       setStatus]       = useState('completed');
  const [date,         setDate]         = useState('');
  const [detailsText,  setDetailsText]  = useState('');
  const [teethText,    setTeethText]    = useState('');
  const [cdtCode,      setCdtCode]      = useState('');
  const [editSurfaces, setEditSurfaces] = useState([]);
  const [anaesthesia,  setAnaesthesia]  = useState('');
  const [materials,    setMaterials]    = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState('');

  // ── add timeline entry ──
  const [showTlForm,  setShowTlForm]  = useState(false);
  const [tlDate,      setTlDate]      = useState(todayISO());
  const [tlTitle,     setTlTitle]     = useState('');
  const [tlNotes,     setTlNotes]     = useState('');
  const [tlSaving,    setTlSaving]    = useState(false);
  const [tlError,     setTlError]     = useState('');

  // ── delete ──
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [delError,    setDelError]    = useState('');

  // Seed all form state whenever a new proc is opened
  useEffect(() => {
    if (!proc) return;
    setEditMode(false);
    setSaveError('');
    setShowTlForm(false);
    setTlTitle(''); setTlNotes(''); setTlDate(todayISO()); setTlError('');
    setConfirmDel(false); setDelError('');
    setProcName(proc.procedureName ?? '');
    setCategory(proc.category ?? 'restorative');
    setStatus(proc.status ?? 'completed');
    setDate(proc.dateOfPublish ? proc.dateOfPublish.split('T')[0] : todayISO());
    setDetailsText((proc.details ?? []).join('\n'));
    setTeethText((proc.teeth ?? []).join(', '));
    setCdtCode(proc.cdtCode ?? '');
    setEditSurfaces(proc.surfaces ?? []);
    setAnaesthesia(proc.anaesthesia ?? '');
    setMaterials(proc.materials ?? '');
    setFollowUpDate(proc.followUpDate ? proc.followUpDate.split('T')[0] : '');
  }, [proc]);

  const toggleEditSurface = (s) => {
    setEditSurfaces(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  // ── SAVE: use PUT (not PATCH) because we include array fields (details, teeth).
  const handleSave = async () => {
    if (!procName.trim()) { setSaveError('Procedure name is required.'); return; }
    setSaving(true);
    setSaveError('');
    try {
      const details = detailsText.split('\n').map(l => l.trim()).filter(Boolean);
      const teeth   = teethText.split(',').map(t => t.trim()).filter(Boolean);
      const body = {
        procedureName: procName.trim(),
        category,
        status,
        dateOfPublish: date,
        details,
        teeth,
        surfaces: editSurfaces,
      };
      if (cdtCode.trim())      body.cdtCode      = cdtCode.trim();
      if (anaesthesia.trim())  body.anaesthesia  = anaesthesia.trim();
      if (materials.trim())    body.materials     = materials.trim();
      if (followUpDate)        body.followUpDate  = followUpDate;

      const { data } = await api.put(`/mhr/dentalProcedureRecords/${proc._id}`, body);
      onSaved(data);
      setEditMode(false);
    } catch (err) {
      setSaveError('Failed to save changes. Please try again.');
      logger.error('ProcedureEditDrawer save error', err);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (proc) {
      setProcName(proc.procedureName ?? '');
      setCategory(proc.category ?? 'restorative');
      setStatus(proc.status ?? 'completed');
      setDate(proc.dateOfPublish ? proc.dateOfPublish.split('T')[0] : todayISO());
      setDetailsText((proc.details ?? []).join('\n'));
      setTeethText((proc.teeth ?? []).join(', '));
      setCdtCode(proc.cdtCode ?? '');
      setEditSurfaces(proc.surfaces ?? []);
      setAnaesthesia(proc.anaesthesia ?? '');
      setMaterials(proc.materials ?? '');
      setFollowUpDate(proc.followUpDate ? proc.followUpDate.split('T')[0] : '');
    }
    setSaveError('');
    setEditMode(false);
  };

  // ── ADD TIMELINE ENTRY: PUT /:id/timeline ──
  const handleAddTimeline = async () => {
    if (!tlTitle.trim()) { setTlError('Visit title is required.'); return; }
    setTlSaving(true);
    setTlError('');
    try {
      const entry = { date: tlDate, title: tlTitle.trim() };
      if (tlNotes.trim()) entry.notes = tlNotes.trim();
      const { data } = await api.put(
        `/mhr/dentalProcedureRecords/${proc._id}/timeline`,
        { entry }
      );
      onSaved(data);
      setShowTlForm(false);
      setTlTitle(''); setTlNotes(''); setTlDate(todayISO());
    } catch (err) {
      setTlError('Failed to add entry. Please try again.');
      logger.error('Add timeline entry error', err);
    } finally {
      setTlSaving(false);
    }
  };

  // ── DELETE: DELETE /:id ──
  const handleDelete = async () => {
    setDeleting(true);
    setDelError('');
    try {
      await api.delete(`/mhr/dentalProcedureRecords/${proc._id}`);
      onDeleted(proc._id);
      onClose();
    } catch (err) {
      setDelError('Failed to delete. Please try again.');
      logger.error('Delete procedure error', err);
      setDeleting(false);
    }
  };

  if (!proc) return null;

  const tagCls = CATEGORY_COLOR[proc.category] ?? 'dc-tag--amber';

  const statusTag = (s) => {
    if (s === 'completed')   return <span className="dc-tag dc-tag--teal"  style={{ fontSize: 12 }}>Completed</span>;
    if (s === 'planned')     return <span className="dc-tag dc-tag--amber" style={{ fontSize: 12 }}>Planned</span>;
    if (s === 'in-progress') return <span className="dc-tag dc-tag--amber" style={{ fontSize: 12 }}>In Progress</span>;
    if (s === 'cancelled')   return <span className="dc-tag dc-tag--red"   style={{ fontSize: 12 }}>Cancelled</span>;
    return null;
  };

  return (
    <>
      <div className={`dc-overlay${isOpen ? ' dc-overlay--show' : ''}`} onClick={onClose} />
      <aside className={`dc-drawer${isOpen ? ' dc-drawer--show' : ''}`}>

        {/* ── Header ── */}
        <div className="dc-drawer__head">
          <div>
            <div className="dc-drawer__title">
              {editMode ? 'Edit Procedure' : proc.procedureName}
            </div>
            <div className="dc-drawer__sub">{fmtDate(proc.dateOfPublish)}</div>
          </div>
          <button className="dc-drawer__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ── Body ── */}
        <div className="dc-drawer__body">

          {/* ════ VIEW MODE ════ */}
          {!editMode && (
            <>
              {/* Category + status tags */}
              <div className="dc-drawer__status">
                <span className={`dc-tag ${tagCls}`} style={{ fontSize: 12 }}>{proc.category}</span>
                {statusTag(proc.status)}
              </div>

              {/* CDT Code */}
              {proc.cdtCode && (
                <>
                  <div className="dc-drawer__label">CDT Code</div>
                  <div className="dc-drawer__note" style={{ padding: '8px 14px' }}>{proc.cdtCode}</div>
                </>
              )}

              {/* Teeth */}
              {proc.teeth?.length > 0 && (
                <>
                  <div className="dc-drawer__label">Teeth</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                    {proc.teeth.map(t => (
                      <span key={t} className="dc-proc-card__tooth-tag">{t}</span>
                    ))}
                  </div>
                </>
              )}

              {/* Surfaces */}
              {proc.surfaces?.length > 0 && (
                <>
                  <div className="dc-drawer__label" style={{ marginTop: 16 }}>Surfaces</div>
                  <div className="dc-surf-chips">
                    {proc.surfaces.map(s => (
                      <span key={s} className="dc-surf-chip dc-surf-chip--on" style={{ cursor: 'default' }}>{s}</span>
                    ))}
                  </div>
                </>
              )}

              {/* Details */}
              {proc.details?.length > 0 && (
                <>
                  <div className="dc-drawer__label" style={{ marginTop: 16 }}>Procedure Details</div>
                  <div className="dc-drawer__note">
                    {proc.details.map((d, i) => (
                      <div key={i} style={{ marginBottom: i < proc.details.length - 1 ? 6 : 0 }}>• {d}</div>
                    ))}
                  </div>
                </>
              )}

              {/* Anaesthesia */}
              {proc.anaesthesia && (
                <>
                  <div className="dc-drawer__label" style={{ marginTop: 16 }}>Anaesthesia</div>
                  <div className="dc-drawer__note" style={{ padding: '8px 14px' }}>{proc.anaesthesia}</div>
                </>
              )}

              {/* Materials */}
              {proc.materials && (
                <>
                  <div className="dc-drawer__label" style={{ marginTop: 16 }}>Materials Used</div>
                  <div className="dc-drawer__note" style={{ padding: '8px 14px' }}>{proc.materials}</div>
                </>
              )}

              {/* Follow-up Date */}
              {proc.followUpDate && (
                <>
                  <div className="dc-drawer__label" style={{ marginTop: 16 }}>Follow-up Date</div>
                  <div className="dc-drawer__note" style={{ padding: '8px 14px' }}>{fmtDate(proc.followUpDate)}</div>
                </>
              )}

              {/* Visit timeline */}
              {proc.timeline?.length > 0 && (
                <>
                  <div className="dc-drawer__label" style={{ marginTop: 16 }}>Visit Timeline</div>
                  <div className="dc-drawer__timeline">
                    {proc.timeline.map((tl, i) => (
                      <div key={i} className="dc-drawer__tl-item">
                        <span className="dc-drawer__tl-dot"
                          style={{ background: '#36a99e', boxShadow: '0 0 0 1px #36a99e' }} />
                        <div className="dc-drawer__tl-content">
                          <div className="dc-drawer__tl-date">{fmtDate(tl.date)}</div>
                          <div className="dc-drawer__tl-title">{tl.title}</div>
                          {tl.notes && <div className="dc-drawer__tl-sub">{tl.notes}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── Add timeline entry ── */}
              <div className="dc-drawer__label" style={{ marginTop: 22 }}>Add Visit</div>
              {!showTlForm ? (
                <button
                  className="dc-btn dc-btn--full dc-btn--ghost"
                  onClick={() => { setShowTlForm(true); setTlError(''); }}
                >
                  + Add Timeline Entry
                </button>
              ) : (
                <div className="dc-drawer__form-section">
                  <div className="dc-field-group">
                    <label className="dc-field-label">
                      Visit Title <span className="dc-field-required">*</span>
                    </label>
                    <input
                      className="dc-field-input"
                      type="text"
                      value={tlTitle}
                      onChange={e => setTlTitle(e.target.value)}
                      placeholder="e.g. Crown cementation"
                    />
                  </div>
                  <div className="dc-field-group">
                    <label className="dc-field-label">Date</label>
                    <input
                      className="dc-field-input"
                      type="date"
                      value={tlDate}
                      onChange={e => setTlDate(e.target.value)}
                    />
                  </div>
                  <div className="dc-field-group">
                    <label className="dc-field-label">
                      Notes <span className="dc-field-optional">(optional)</span>
                    </label>
                    <textarea
                      className="dc-field-textarea"
                      value={tlNotes}
                      onChange={e => setTlNotes(e.target.value)}
                      rows={3}
                      placeholder="What was done in this visit…"
                    />
                  </div>
                  {tlError && <p className="dc-field-error">{tlError}</p>}
                  <div className="dc-form-actions">
                    <button
                      className="dc-btn dc-btn--ghost"
                      onClick={() => { setShowTlForm(false); setTlError(''); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="dc-btn dc-btn--primary"
                      onClick={handleAddTimeline}
                      disabled={tlSaving}
                    >
                      {tlSaving ? 'Saving…' : 'Add Entry'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Edit / Delete ── */}
              <div className="dc-drawer__label" style={{ marginTop: 22 }}>Actions</div>
              <button
                className="dc-btn dc-btn--full dc-btn--primary"
                onClick={() => { setEditMode(true); setConfirmDel(false); }}
              >
                ✏ Edit Procedure
              </button>

              {!confirmDel ? (
                <button
                  className="dc-btn dc-btn--full dc-btn--danger"
                  onClick={() => setConfirmDel(true)}
                >
                  Delete Procedure
                </button>
              ) : (
                <div className="dc-drawer__delete-confirm">
                  <p className="dc-drawer__delete-msg">
                    Permanently delete this procedure? This cannot be undone.
                  </p>
                  {delError && <p className="dc-field-error">{delError}</p>}
                  <div className="dc-form-actions">
                    <button
                      className="dc-btn dc-btn--ghost"
                      onClick={() => { setConfirmDel(false); setDelError(''); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="dc-btn dc-btn--danger"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting…' : 'Confirm Delete'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ════ EDIT MODE ════ */}
          {editMode && (
            <div className="dc-drawer__form-section">
              <div className="dc-field-group">
                <label className="dc-field-label">
                  Procedure Name <span className="dc-field-required">*</span>
                </label>
                <input
                  className="dc-field-input"
                  type="text"
                  value={procName}
                  onChange={e => setProcName(e.target.value)}
                  placeholder="e.g. Composite Filling"
                />
              </div>

              <div className="dc-field-row">
                <div className="dc-field-group">
                  <label className="dc-field-label">Category</label>
                  <select
                    className="dc-field-select"
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="dc-field-group">
                  <label className="dc-field-label">CDT Code <span className="dc-field-optional">(opt.)</span></label>
                  <input
                    className="dc-field-input"
                    type="text"
                    value={cdtCode}
                    onChange={e => setCdtCode(e.target.value)}
                    placeholder="e.g. D2390"
                  />
                </div>
              </div>

              <div className="dc-field-row">
                <div className="dc-field-group">
                  <label className="dc-field-label">Date</label>
                  <input
                    className="dc-field-input"
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                  />
                </div>
                <div className="dc-field-group">
                  <label className="dc-field-label">Status</label>
                  <select
                    className="dc-field-select"
                    value={status}
                    onChange={e => setStatus(e.target.value)}
                  >
                    {STATUSES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="dc-field-group">
                <label className="dc-field-label">
                  Teeth <span className="dc-field-optional">(FDI numbers, comma-separated)</span>
                </label>
                <input
                  className="dc-field-input"
                  type="text"
                  value={teethText}
                  onChange={e => setTeethText(e.target.value)}
                  placeholder="e.g. 36, 46"
                />
              </div>

              <div className="dc-field-group">
                <label className="dc-field-label">Surfaces <span className="dc-field-optional">(optional)</span></label>
                <div className="dc-surf-chips">
                  {SURFACES.map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`dc-surf-chip${editSurfaces.includes(s) ? ' dc-surf-chip--on' : ''}`}
                      onClick={() => toggleEditSurface(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="dc-field-group">
                <label className="dc-field-label">
                  Details <span className="dc-field-optional">(one per line)</span>
                </label>
                <textarea
                  className="dc-field-textarea"
                  value={detailsText}
                  onChange={e => setDetailsText(e.target.value)}
                  rows={4}
                  placeholder={`e.g.\nCaries excavated\nComposite resin placed`}
                />
              </div>

              <div className="dc-field-row">
                <div className="dc-field-group">
                  <label className="dc-field-label">Anaesthesia <span className="dc-field-optional">(opt.)</span></label>
                  <input
                    className="dc-field-input"
                    type="text"
                    value={anaesthesia}
                    onChange={e => setAnaesthesia(e.target.value)}
                    placeholder="e.g. 2% Lidocaine"
                  />
                </div>
                <div className="dc-field-group">
                  <label className="dc-field-label">Materials <span className="dc-field-optional">(opt.)</span></label>
                  <input
                    className="dc-field-input"
                    type="text"
                    value={materials}
                    onChange={e => setMaterials(e.target.value)}
                    placeholder="e.g. Composite resin"
                  />
                </div>
              </div>

              <div className="dc-field-group">
                <label className="dc-field-label">Follow-up Date <span className="dc-field-optional">(optional)</span></label>
                <input
                  className="dc-field-input"
                  type="date"
                  value={followUpDate}
                  onChange={e => setFollowUpDate(e.target.value)}
                />
              </div>

              {saveError && <p className="dc-field-error">{saveError}</p>}

              <div className="dc-form-actions">
                <button className="dc-btn dc-btn--ghost" onClick={cancelEdit}>Cancel</button>
                <button
                  className="dc-btn dc-btn--primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

        </div>
      </aside>
    </>
  );
}

// ─────────────────────────────────────────────
// ADD PROCEDURE MODAL  (centered full-form popup)
// ─────────────────────────────────────────────

function AddProcedureModal({
  isOpen, onClose, onSaved,
  defaultFdi, dentitionMode,
  patientId, doctorId, clinicId,
}) {
  const { selectedAppointment } = useApp();
  const [procName,     setProcName]     = useState('');
  const [category,     setCategory]     = useState('restorative');
  const [cdtCode,      setCdtCode]      = useState('');
  const [date,         setDate]         = useState(todayISO());
  const [status,       setStatus]       = useState('completed');
  const [selectedTeeth,setSelectedTeeth]= useState([]);
  const [surfaces,     setSurfaces]     = useState([]);
  const [details,      setDetails]      = useState('');
  const [anaesthesia,  setAnaesthesia]  = useState('');
  const [materials,    setMaterials]    = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [timeline,     setTimeline]     = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  // Timeline entry form
  const [showTlForm, setShowTlForm] = useState(false);
  const [tlDate,     setTlDate]     = useState(todayISO());
  const [tlTitle,    setTlTitle]    = useState('');
  const [tlNotes,    setTlNotes]    = useState('');
  const [tlError,    setTlError]    = useState('');

  // Reset all fields each time the modal opens (with the new defaultFdi)
  useEffect(() => {
    if (!isOpen) return;
    setProcName('');
    setCategory('restorative');
    setCdtCode('');
    setDate(todayISO());
    setStatus('completed');
    setSelectedTeeth(defaultFdi ? [String(defaultFdi)] : []);
    setSurfaces([]);
    setDetails('');
    setAnaesthesia('');
    setMaterials('');
    setFollowUpDate('');
    setTimeline([]);
    setError('');
    setShowTlForm(false);
    setTlTitle(''); setTlNotes(''); setTlDate(todayISO()); setTlError('');
  }, [isOpen, defaultFdi]);

  const cfg = DENTITION_CONFIG[dentitionMode] ?? DENTITION_CONFIG.adult;

  const toggleTooth = (fdi) => {
    setSelectedTeeth(prev =>
      prev.includes(fdi) ? prev.filter(t => t !== fdi) : [...prev, fdi]
    );
  };

  const toggleSurface = (s) => {
    setSurfaces(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const addTlEntry = () => {
    if (!tlTitle.trim()) { setTlError('Visit title is required.'); return; }
    const entry = { date: tlDate, title: tlTitle.trim() };
    if (tlNotes.trim()) entry.notes = tlNotes.trim();
    setTimeline(prev => [...prev, entry]);
    setTlTitle(''); setTlNotes(''); setTlDate(todayISO());
    setShowTlForm(false); setTlError('');
  };

  const handleSave = async () => {
    if (!procName.trim()) { setError('Procedure name is required.'); return; }
    if (!patientId)       { setError('No patient selected.');        return; }
    setSaving(true); setError('');
    try {
      const detailLines = details.split('\n').map(l => l.trim()).filter(Boolean);
      const body = {
        patientId,
        dateOfPublish:  date,
        procedureName:  procName.trim(),
        category,
        teeth:          selectedTeeth,
        details:        detailLines,
        status,
        surfaces:       surfaces.length > 0 ? surfaces : undefined,
        timeline:       timeline.length  > 0 ? timeline  : undefined,
      };
      if (doctorId)          body.doctorId     = doctorId;
      if (clinicId)          body.clinicId     = clinicId;
      if (cdtCode.trim())    body.cdtCode      = cdtCode.trim();
      if (anaesthesia.trim())body.anaesthesia  = anaesthesia.trim();
      if (materials.trim())  body.materials    = materials.trim();
      if (followUpDate)         body.followUpDate  = followUpDate;
      if (selectedAppointment)  body.appointmentId = selectedAppointment;

      const { data } = await api.post('/mhr/dentalProcedureRecords', body);
      onSaved(data);
      onClose();
    } catch (err) {
      setError('Failed to save procedure. Please try again.');
      logger.error('AddProcedureModal save error', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="dc-modal-overlay"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="dc-modal">

        {/* ── Header ── */}
        <div className="dc-modal__head">
          <div>
            <h2 className="dc-modal__title">Add Procedure</h2>
            <p className="dc-modal__sub">Record a new dental treatment</p>
          </div>
          <button className="dc-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ── Body ── */}
        <div className="dc-modal__body">

          {/* Procedure Name + Category */}
          <div className="dc-field-row">
            <div className="dc-field-group">
              <label className="dc-field-label">
                Procedure Name <span className="dc-field-required">*</span>
              </label>
              <input
                className="dc-field-input"
                type="text"
                value={procName}
                onChange={e => setProcName(e.target.value)}
                placeholder="e.g. Composite Filling"
                autoFocus
              />
            </div>
            <div className="dc-field-group">
              <label className="dc-field-label">Category</label>
              <select
                className="dc-field-select"
                value={category}
                onChange={e => setCategory(e.target.value)}
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* CDT Code + Date */}
          <div className="dc-field-row">
            <div className="dc-field-group">
              <label className="dc-field-label">
                CDT Code <span className="dc-field-optional">(optional)</span>
              </label>
              <input
                className="dc-field-input"
                type="text"
                value={cdtCode}
                onChange={e => setCdtCode(e.target.value)}
                placeholder="e.g. D2390"
              />
            </div>
            <div className="dc-field-group">
              <label className="dc-field-label">Date</label>
              <input
                className="dc-field-input"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </div>
          </div>

          {/* Status — segmented control */}
          <div className="dc-field-group">
            <label className="dc-field-label">Status</label>
            <div className="dc-status-steps">
              {STATUSES.map(s => (
                <button
                  key={s.value}
                  type="button"
                  className={`dc-sstep${status === s.value ? ' dc-sstep--on' : ''}`}
                  onClick={() => setStatus(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tooth chip selector */}
          <div className="dc-field-group">
            <label className="dc-field-label">
              Teeth <span className="dc-field-optional">(click to select · multiple allowed)</span>
            </label>
            <div className="dc-modal__teeth-section">
              <p className="dc-modal__arch-label">Upper · Maxillary</p>
              <div className="dc-tooth-chips">
                {cfg.upper.map(fdi => {
                  const f = String(fdi);
                  return (
                    <button
                      key={f}
                      type="button"
                      className={`dc-tooth-chip${selectedTeeth.includes(f) ? ' dc-tooth-chip--sel' : ''}`}
                      onClick={() => toggleTooth(f)}
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
              <p className="dc-modal__arch-label" style={{ marginTop: 10 }}>Lower · Mandibular</p>
              <div className="dc-tooth-chips">
                {cfg.lower.map(fdi => {
                  const f = String(fdi);
                  return (
                    <button
                      key={f}
                      type="button"
                      className={`dc-tooth-chip${selectedTeeth.includes(f) ? ' dc-tooth-chip--sel' : ''}`}
                      onClick={() => toggleTooth(f)}
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Surfaces */}
          <div className="dc-field-group">
            <label className="dc-field-label">
              Surfaces <span className="dc-field-optional">(optional)</span>
            </label>
            <div className="dc-surf-chips">
              {SURFACES.map(s => (
                <button
                  key={s}
                  type="button"
                  className={`dc-surf-chip${surfaces.includes(s) ? ' dc-surf-chip--on' : ''}`}
                  onClick={() => toggleSurface(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Clinical Notes / Details */}
          <div className="dc-field-group">
            <label className="dc-field-label">
              Clinical Notes / Details{' '}
              <span className="dc-field-optional">(one per line)</span>
            </label>
            <textarea
              className="dc-field-textarea"
              value={details}
              onChange={e => setDetails(e.target.value)}
              rows={3}
              placeholder={`e.g.\nCaries excavated\nComposite resin placed\nOcclusion checked`}
            />
          </div>

          {/* Anaesthesia + Materials */}
          <div className="dc-field-row">
            <div className="dc-field-group">
              <label className="dc-field-label">
                Anaesthesia <span className="dc-field-optional">(optional)</span>
              </label>
              <input
                className="dc-field-input"
                type="text"
                value={anaesthesia}
                onChange={e => setAnaesthesia(e.target.value)}
                placeholder="e.g. 2% Lidocaine"
              />
            </div>
            <div className="dc-field-group">
              <label className="dc-field-label">
                Materials Used <span className="dc-field-optional">(optional)</span>
              </label>
              <input
                className="dc-field-input"
                type="text"
                value={materials}
                onChange={e => setMaterials(e.target.value)}
                placeholder="e.g. Composite resin"
              />
            </div>
          </div>

          {/* Follow-up Date */}
          <div className="dc-field-group">
            <label className="dc-field-label">
              Follow-up Date <span className="dc-field-optional">(optional)</span>
            </label>
            <input
              className="dc-field-input"
              type="date"
              value={followUpDate}
              onChange={e => setFollowUpDate(e.target.value)}
            />
          </div>

          {/* Visit Timeline */}
          <div className="dc-field-group">
            <label className="dc-field-label">
              Visit Timeline{' '}
              <span className="dc-field-optional">(optional — for multi-visit procedures)</span>
            </label>

            {timeline.length > 0 && (
              <div className="dc-tl-entry-list">
                {timeline.map((entry, i) => (
                  <div key={i} className="dc-tl-entry-item">
                    <div className="dc-tl-entry-info">
                      <span className="dc-tl-entry-date">{fmtDate(entry.date)}</span>
                      <span className="dc-tl-entry-title">{entry.title}</span>
                      {entry.notes && (
                        <span className="dc-tl-entry-notes">{entry.notes}</span>
                      )}
                    </div>
                    <button
                      className="dc-tl-entry-remove"
                      type="button"
                      onClick={() => setTimeline(prev => prev.filter((_, j) => j !== i))}
                      aria-label="Remove visit"
                    >×</button>
                  </div>
                ))}
              </div>
            )}

            {!showTlForm ? (
              <button
                className="dc-btn dc-btn--ghost dc-tl-add-btn"
                type="button"
                onClick={() => { setShowTlForm(true); setTlError(''); }}
              >
                + Add Visit
              </button>
            ) : (
              <div className="dc-tl-entry-form">
                <div className="dc-field-group">
                  <label className="dc-field-label">
                    Visit Title <span className="dc-field-required">*</span>
                  </label>
                  <input
                    className="dc-field-input"
                    type="text"
                    value={tlTitle}
                    onChange={e => setTlTitle(e.target.value)}
                    placeholder="e.g. Initial preparation"
                  />
                </div>
                <div className="dc-field-row">
                  <div className="dc-field-group">
                    <label className="dc-field-label">Date</label>
                    <input
                      className="dc-field-input"
                      type="date"
                      value={tlDate}
                      onChange={e => setTlDate(e.target.value)}
                    />
                  </div>
                  <div className="dc-field-group">
                    <label className="dc-field-label">
                      Notes <span className="dc-field-optional">(optional)</span>
                    </label>
                    <input
                      className="dc-field-input"
                      type="text"
                      value={tlNotes}
                      onChange={e => setTlNotes(e.target.value)}
                      placeholder="What was done…"
                    />
                  </div>
                </div>
                {tlError && <p className="dc-field-error">{tlError}</p>}
                <div className="dc-form-actions">
                  <button
                    className="dc-btn dc-btn--ghost"
                    type="button"
                    onClick={() => { setShowTlForm(false); setTlError(''); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="dc-btn dc-btn--primary"
                    type="button"
                    onClick={addTlEntry}
                  >
                    Add Visit
                  </button>
                </div>
              </div>
            )}
          </div>

          {error && <p className="dc-field-error" style={{ marginBottom: 8 }}>{error}</p>}
        </div>

        {/* ── Footer ── */}
        <div className="dc-modal__foot">
          <button className="dc-btn dc-btn--ghost" onClick={onClose}>Cancel</button>
          <button
            className="dc-btn dc-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Procedure'}
          </button>
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// TOOTH DRAWER  (simplified — add-proc delegated to modal)
// ─────────────────────────────────────────────

function ToothDrawer({
  fdi, teethMap, chartId, procedures,
  isOpen, onClose, onUpdate, onProcedureAdded,
  onAddProcedure,
}) {
  const [markMode,   setMarkMode]   = useState(false);
  const [newCond,    setNewCond]    = useState('caries');
  const [condNote,   setCondNote]   = useState('');
  const [condSaving, setCondSaving] = useState(false);
  const [condError,  setCondError]  = useState('');

  const toothData = fdi ? (teethMap[String(fdi)] ?? null) : null;
  const condition = toothData?.condition ?? 'healthy';
  const type      = fdi ? getToothType(fdi) : '';
  const timeline  = buildTimeline(procedures, fdi);

  // Reset sub-forms when tooth changes or drawer closes
  useEffect(() => {
    setMarkMode(false);
    setCondError('');
    if (fdi) {
      setNewCond(condition);
      setCondNote(toothData?.note ?? '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fdi]);

  // ── SAVE: mark condition ──
  const handleCondSave = async () => {
    if (!chartId || !fdi) return;
    setCondSaving(true);
    setCondError('');
    try {
      await api.patch(`/mhr/dentalChartRecords/${chartId}/tooth/${fdi}`, {
        condition: newCond,
        ...(condNote ? { note: condNote } : {}),
      });
      onUpdate({ fdi, condition: newCond, note: condNote });
      setMarkMode(false);
    } catch (err) {
      setCondError('Failed to save. Please try again.');
      logger.error('Tooth update error', err);
    } finally {
      setCondSaving(false);
    }
  };

  if (!fdi) return null;

  return (
    <>
      <div
        className={`dc-overlay${isOpen ? ' dc-overlay--show' : ''}`}
        onClick={onClose}
      />
      <aside className={`dc-drawer${isOpen ? ' dc-drawer--show' : ''}`}>

        {/* ── Header ── */}
        <div className="dc-drawer__head">
          <div>
            <div className="dc-drawer__title">Tooth {fdi}</div>
            <div className="dc-drawer__sub">
              {toothTypeName(type)} · {isPrimary(fdi) ? 'Primary (deciduous)' : 'Permanent'}
            </div>
          </div>
          <button className="dc-drawer__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ── Body ── */}
        <div className="dc-drawer__body">

          {/* Current condition badge — always visible */}
          <div className="dc-drawer__status">
            <span
              className="dc-drawer__dot"
              style={{ background: COND[condition]?.c, borderColor: COND[condition]?.border }}
            />
            <span className="dc-drawer__status-txt">{COND[condition]?.l}</span>
          </div>

          {/* ════════ DEFAULT VIEW ════════ */}
          {!markMode && (
            <>
              {/* Clinical note from chart */}
              {toothData?.note && (
                <>
                  <div className="dc-drawer__label">Clinical Note</div>
                  <div className="dc-drawer__note">{toothData.note}</div>
                </>
              )}

              {/* Treatment timeline */}
              {timeline.length > 0 ? (
                <>
                  <div className="dc-drawer__label">Treatment Timeline</div>
                  <div className="dc-drawer__timeline">
                    {timeline.map((entry, i) => {
                      const dotColor = CATEGORY_COLOR[entry.category] === 'dc-tag--red'
                        ? '#e8554e'
                        : CATEGORY_COLOR[entry.category] === 'dc-tag--amber'
                        ? '#f6b81e'
                        : '#36a99e';
                      return (
                        <div key={i} className="dc-drawer__tl-item">
                          <span
                            className="dc-drawer__tl-dot"
                            style={{ background: dotColor, boxShadow: `0 0 0 1px ${dotColor}` }}
                          />
                          <div className="dc-drawer__tl-content">
                            <div className="dc-drawer__tl-date">{fmtDate(entry.date)}</div>
                            <div className="dc-drawer__tl-title">{entry.title}</div>
                            {entry.subtitle && (
                              <div className="dc-drawer__tl-sub">{entry.subtitle}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                !toothData && (
                  <div className="dc-drawer__empty">
                    No recorded issues for this tooth.<br />
                    It appears healthy at the most recent examination.
                  </div>
                )
              )}

              {/* Actions */}
              <div className="dc-drawer__label" style={{ marginTop: 22 }}>Actions</div>
              <button
                className="dc-btn dc-btn--full dc-btn--primary"
                onClick={() => { onAddProcedure(fdi); onClose(); }}
              >
                + Add Procedure
              </button>
              <button
                className="dc-btn dc-btn--full dc-btn--ghost"
                onClick={() => { setMarkMode(true); setCondError(''); }}
              >
                Mark / Update Condition
              </button>
            </>
          )}

          {/* ════════ MARK CONDITION FORM ════════ */}
          {markMode && (
            <div className="dc-drawer__form-section">
              <div className="dc-drawer__label">Select Condition</div>
              <div className="dc-cond-grid">
                {CONDITIONS_LIST.map(c => (
                  <button
                    key={c.value}
                    className={`dc-cond-btn${newCond === c.value ? ' dc-cond-btn--on' : ''}`}
                    style={newCond === c.value
                      ? { borderColor: c.color, background: c.color + '22' }
                      : {}}
                    onClick={() => setNewCond(c.value)}
                  >
                    <span className="dc-cond-swatch" style={{ background: c.color }} />
                    {c.label}
                  </button>
                ))}
              </div>

              <div className="dc-drawer__label" style={{ marginTop: 16 }}>
                Clinical Note <span className="dc-field-optional">(optional)</span>
              </div>
              <textarea
                className="dc-field-textarea"
                value={condNote}
                onChange={e => setCondNote(e.target.value)}
                placeholder="Add or update clinical note…"
                rows={3}
              />

              {condError && <p className="dc-field-error">{condError}</p>}

              <div className="dc-form-actions">
                <button
                  className="dc-btn dc-btn--ghost"
                  onClick={() => { setMarkMode(false); setCondError(''); }}
                >
                  Cancel
                </button>
                <button
                  className="dc-btn dc-btn--primary"
                  onClick={handleCondSave}
                  disabled={condSaving}
                >
                  {condSaving ? 'Saving…' : 'Save Condition'}
                </button>
              </div>
            </div>
          )}

        </div>
      </aside>
    </>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

export default function DentalChart() {
  const { selectedPatient, doctor, selectedClinic } = useApp();
  const { user } = useAuth();

  const [mode,           setMode]           = useState('adult');
  const [chartId,        setChartId]        = useState(null);
  const [teethMap,       setTeethMap]       = useState({});
  const [summary,        setSummary]        = useState(null);
  const [procedures,     setProcedures]     = useState([]);
  const [chartLoading,   setChartLoading]   = useState(false);
  const [procLoading,    setProcLoading]    = useState(false);
  const [selectedFdi,    setSelectedFdi]    = useState(null);
  const [drawerOpen,     setDrawerOpen]     = useState(false);

  // Procedure edit drawer
  const [selectedProc,   setSelectedProc]   = useState(null);
  const [procEditOpen,   setProcEditOpen]   = useState(false);

  // Add procedure modal
  const [addProcModalOpen,  setAddProcModalOpen]  = useState(false);
  const [addProcDefaultFdi, setAddProcDefaultFdi] = useState(null);

  const buildMap = useCallback((teeth = []) =>
    Object.fromEntries(teeth.map(t => [String(t.fdi), t])),
  []);

  const fetchChart = useCallback(async (patientId, dentitionType) => {
    if (!patientId) return;
    setChartLoading(true);
    try {
      const { data } = await api.get(
        `/mhr/dentalChartRecords/patient/${patientId}/latest`,
        { params: { dentitionType } }
      );
      setChartId(data._id ?? null);
      setTeethMap(buildMap(data.teeth));
      setSummary(data.summary ?? null);
    } catch (err) {
      logger.error('fetchChart error', err);
    } finally {
      setChartLoading(false);
    }
  }, [buildMap]);

  const fetchProcedures = useCallback(async (patientId) => {
    if (!patientId) return;
    setProcLoading(true);
    try {
      const { data } = await api.get(`/mhr/dentalProcedureRecords/patient/${patientId}`);
      setProcedures(Array.isArray(data) ? data : []);
    } catch (err) {
      logger.error('fetchProcedures error', err);
      setProcedures([]);
    } finally {
      setProcLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChart(selectedPatient, mode);
  }, [selectedPatient, mode, fetchChart]);

  useEffect(() => {
    fetchProcedures(selectedPatient);
  }, [selectedPatient, fetchProcedures]);

  const openTooth = (fdi) => {
    setSelectedFdi(fdi);
    setDrawerOpen(true);
  };

  const closeTooth = () => {
    setDrawerOpen(false);
    setSelectedFdi(null);
  };

  const handleToothUpdate = useCallback(({ fdi, condition, note }) => {
    setTeethMap(prev => {
      const next = { ...prev };
      if (condition === 'healthy') {
        delete next[String(fdi)];
      } else {
        next[String(fdi)] = { ...(next[String(fdi)] ?? {}), fdi: String(fdi), condition, note };
      }
      return next;
    });
    fetchChart(selectedPatient, mode);
  }, [selectedPatient, mode, fetchChart]);

  const handleProcedureAdded = useCallback((newProc) => {
    setProcedures(prev => [newProc, ...prev]);
  }, []);

  // Open the Add Procedure modal (called by ToothDrawer's "+ Add Procedure" button)
  const openAddProcModal = useCallback((fdi) => {
    setAddProcDefaultFdi(fdi ?? null);
    setAddProcModalOpen(true);
  }, []);

  const closeAddProcModal = useCallback(() => {
    setAddProcModalOpen(false);
    setAddProcDefaultFdi(null);
  }, []);

  // Open procedure edit drawer
  const openProcEdit = useCallback((proc) => {
    setSelectedProc(proc);
    setProcEditOpen(true);
  }, []);

  const closeProcEdit = useCallback(() => {
    setProcEditOpen(false);
    setSelectedProc(null);
  }, []);

  const handleProcSaved = useCallback((updated) => {
    setProcedures(prev =>
      prev.map(p => (p._id === updated._id ? updated : p))
    );
    setSelectedProc(updated);
  }, []);

  const handleProcDeleted = useCallback((deletedId) => {
    setProcedures(prev => prev.filter(p => p._id !== deletedId));
  }, []);

  const cfg = DENTITION_CONFIG[mode];

  const healthy = summary?.healthy ?? cfg.totalTeeth;
  const caries  = summary?.caries  ?? 0;
  const treated = summary?.treated ?? 0;
  const missing = summary?.missing ?? 0;

  const doctorId = user?._id ?? doctor?._id ?? null;
  const clinicId = selectedClinic?._id ?? selectedClinic ?? null;

  return (
    <div className="dc-root">

      {/* ── STATS ── */}
      <div className="dc-stats">
        <StatBox
          value={healthy} label="Healthy teeth" bg="#36a99e"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2"><path d="M20 6L9 17l-5-5"/></svg>}
        />
        <StatBox
          value={caries} label="Active caries" bg="#e8554e"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>}
        />
        <StatBox
          value={treated} label="Treated / restored" bg="#4a90d9"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M3 7l9-4 9 4-9 4-9-4zM3 7v10l9 4 9-4V7"/></svg>}
        />
        <StatBox
          value={missing} label="Missing" bg="#c4ccd2"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>}
        />
      </div>

      {/* ── ODONTOGRAM CARD ── */}
      <div className="dc-card">
        <div className="dc-card__head">
          <h3 className="dc-card__title">
            <svg viewBox="0 0 32 32" fill="none" width="24" height="24">
              <path
                d="M16 4c-3 0-4 2-7 2S4 5 3 7c-1 3 0 7 1 11 1 3 1 7 3 7s2-5 4-5 2 5 5 5 3-5 5-5 1 4 3 4 2-4 3-7c1-4 2-8 1-11-1-2-3-1-6-1s-4-2-7-2z"
                fill="#e9f6f4" stroke="#36a99e" strokeWidth="1.6"
              />
            </svg>
            Odontogram
          </h3>

          <div className="dc-toggle">
            {Object.entries(DENTITION_CONFIG).map(([key, val]) => (
              <button
                key={key}
                className={`dc-toggle__btn${mode === key ? ' dc-toggle__btn--on' : ''}`}
                onClick={() => setMode(key)}
              >
                {val.label}
              </button>
            ))}
          </div>
        </div>

        {chartLoading ? (
          <div className="dc-spinner">Loading chart…</div>
        ) : (
          <div className="dc-arch-wrap">
            <p className="dc-arch-label">Upper · Maxillary</p>
            <Arch
              fdiList={cfg.upper}
              teethMap={teethMap}
              isLower={false}
              selectedFdi={selectedFdi}
              onSelect={openTooth}
            />
            <p className="dc-arch-label" style={{ marginTop: 24 }}>Lower · Mandibular</p>
            <Arch
              fdiList={cfg.lower}
              teethMap={teethMap}
              isLower={true}
              selectedFdi={selectedFdi}
              onSelect={openTooth}
            />
          </div>
        )}

        {/* Legend */}
        <div className="dc-legend">
          {Object.entries(COND).map(([k, v]) => (
            <div key={k} className="dc-legend__item">
              <span className="dc-legend__sw" style={{ background: v.c }} />
              {v.l}
            </div>
          ))}
          <div className="dc-legend__item dc-legend__hint">
            ! = has clinical note · click any tooth to view / add procedure
          </div>
        </div>
      </div>

      {/* ── TREATMENT HISTORY ── */}
      <div className="dc-section-header">
        <h3 className="dc-section-title">Treatment History</h3>
        <button className="dc-btn-new-proc" onClick={() => openAddProcModal(null)}>
          + New Procedure
        </button>
      </div>

      {procLoading ? (
        <div className="dc-spinner">Loading procedures…</div>
      ) : procedures.length === 0 ? (
        <div className="dc-empty">No treatment procedures recorded yet.</div>
      ) : (
        <div className="dc-proc-grid">
          {procedures.map(proc => (
            <ProcedureCard
              key={proc._id ?? proc.recordId}
              proc={proc}
              onClick={() => openProcEdit(proc)}
            />
          ))}
        </div>
      )}

      {/* ── ADD PROCEDURE MODAL ── */}
      <AddProcedureModal
        isOpen={addProcModalOpen}
        onClose={closeAddProcModal}
        onSaved={handleProcedureAdded}
        defaultFdi={addProcDefaultFdi}
        dentitionMode={mode}
        patientId={selectedPatient}
        doctorId={doctorId}
        clinicId={clinicId}
      />

      {/* ── PROCEDURE EDIT DRAWER ── */}
      <ProcedureEditDrawer
        proc={selectedProc}
        isOpen={procEditOpen}
        onClose={closeProcEdit}
        onSaved={handleProcSaved}
        onDeleted={handleProcDeleted}
      />

      {/* ── TOOTH DRAWER ── */}
      <ToothDrawer
        fdi={selectedFdi}
        teethMap={teethMap}
        chartId={chartId}
        procedures={procedures}
        isOpen={drawerOpen}
        onClose={closeTooth}
        onUpdate={handleToothUpdate}
        onProcedureAdded={handleProcedureAdded}
        onAddProcedure={openAddProcModal}
      />
    </div>
  );
}
